import { useNavigate } from "react-router-dom";
import { ComponentPropsWithoutRef, useCallback, useMemo, useRef } from "react";
import { isMatrixCommandStep, isParallelCommandStep } from "~/lib/pipeline";
import Icon from "~/components/shared/Icon";
import { CommandJob } from "~/components/build/Show/lib/types";
import { useTrack } from "~/components/build/Show/lib/useTrack";
import {
  findJobsForStep,
  mostRecentMatrixJobs,
  mostRecentParallelJobs,
  useBuild,
} from "~/components/build/Show/lib/BuildContext";
import useQueryParams from "~/components/build/Show/lib/useQueryParams";
import { resolveJobUrl, resolveStepUrl } from "~/components/build/Show/lib/urlForView";
import {
  FilterByOption,
  filterSteps,
} from "~/components/build/Show/components/Sidebar/useFilterStore";
import { twMerge } from "tailwind-merge";
import { useSteps } from "~/components/build/Show/lib/useSteps";

/**
 * Get the list of current failing steps (and jobs) in the build path.
 *
 * @returns
 */
export const useFailurePaths = (): { step: string; job?: string }[] => {
  const { build } = useBuild();
  const { steps } = useSteps();

  return useMemo(() => {
    return (
      filterSteps(steps, [FilterByOption.Failed, FilterByOption.SoftFailed], build)
        .filter((step) => step.type !== "group")
        .flatMap((step) => {
          // Non-sidebar canvas only supports jumping to steps rather than jobs.
          if (!Features.BuildSidebar) {
            return { step: step.uuid };
          }

          // Find the latest parallel failed jobs for the step.
          if (isParallelCommandStep(step)) {
            const jobs = findJobsForStep<CommandJob>(build, step.uuid).filter(
              (job) => job.state === "finished" && !job.passed,
            );
            return mostRecentParallelJobs(jobs).map((job) => ({
              job: job.id,
              step: step.uuid,
            }));
          }

          // Find the latest matrix failed jobs for the step.
          if (isMatrixCommandStep(step)) {
            const jobs = findJobsForStep<CommandJob>(build, step.uuid).filter(
              (job) => job.state === "finished" && !job.passed,
            );
            return mostRecentMatrixJobs(jobs).map((job) => ({
              job: job.id,
              step: step.uuid,
            }));
          }

          return { step: step.uuid };
        }) || []
    );
  }, [steps, build?.jobs]);
};

interface GoToFailureProps extends Omit<ComponentPropsWithoutRef<"button">, "onClick"> {
  className?: string;
}

export const useFocusOnFailure = () => {
  const failures = useFailurePaths();
  const { stepUuid, jobUuid } = useQueryParams();
  const navigate = useNavigate();
  const track = useTrack();

  const focusOnFailure = useCallback(() => {
    if (!failures.length) {
      return;
    }

    let nextFailure: { step: string; job?: string };

    const current = failures.find((failure) => {
      if (jobUuid) {
        return failure.job === jobUuid;
      }
      if (stepUuid) {
        return failure.step === stepUuid;
      }
      return false;
    });

    if (!current) {
      nextFailure = failures[0];
    } else {
      const idx = failures.indexOf(current);

      if (idx + 1 < failures.length) {
        nextFailure = failures[idx + 1];
      } else {
        nextFailure = failures[0];
      }
    }

    // Navigate to the step or job based on the failure.
    if (nextFailure.job) {
      navigate(resolveJobUrl(nextFailure.job), { state: { forceFocus: true } });
    } else {
      navigate(resolveStepUrl(nextFailure.step), {
        state: { forceFocus: true },
      });
    }
  }, [failures, stepUuid, jobUuid, navigate, track]);

  return focusOnFailure;
};

export const GoToFailure = ({ className = "", ...props }: GoToFailureProps) => {
  const ref = useRef<HTMLButtonElement>(null);
  const failures = useFailurePaths();
  const track = useTrack();
  const focusOnFailure = useFocusOnFailure();

  const handleClick = useCallback(() => {
    focusOnFailure();
    track("Click Go To Failure");

    // Blur the button to prevent focus issues when navigating.
    if (ref.current) {
      ref.current.blur();
    }
  }, [focusOnFailure, track]);

  return (
    <button
      {...props}
      ref={ref}
      onClick={handleClick}
      disabled={!failures.length}
      title="Go to next failure (F)"
      className={twMerge(
        "hover:bg-text-600 group/button text-charcoal-900 inline-flex h-7 w-7 items-center justify-center rounded-md bg-gray-100 font-medium transition-all hover:bg-purple-100 disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
    >
      <Icon
        icon="custom/outline/redo-dot"
        className="h-4 w-4 group-hover/button:stroke-purple-600"
      />
    </button>
  );
};
